import type { MembershipProject } from "@zworks/console-contracts";
import { Check, ChevronsUpDown, Plus } from "lucide-react";
import * as React from "react";
import { cn } from "@/lib/utils";

const tenantTones = ["bg-tn1", "bg-tn2", "bg-tn3", "bg-tn4", "bg-tn5", "bg-tn6"] as const;

/** Two letters for a tenant mark. "Orion Payments" becomes "OP". */
function tenantMark(name: string): string {
  const words = name.split(/\s+/).filter((word) => word.length > 0);
  const letters = words.slice(0, 2).map((word) => word.charAt(0).toUpperCase());
  return letters.join("") || name.slice(0, 1).toUpperCase();
}

function toneFor(id: string): (typeof tenantTones)[number] {
  const total = id.split("").reduce((sum, char) => sum + char.charCodeAt(0), 0);
  return tenantTones[total % tenantTones.length] ?? "bg-tn1";
}

export function TenantMark({
  name,
  id,
  className,
}: {
  name: string;
  id: string;
  className?: string;
}) {
  return (
    <span
      aria-hidden
      className={cn(
        "grid size-6 shrink-0 place-items-center rounded-md text-xs font-semibold text-tn-fg",
        toneFor(id),
        className,
      )}
    >
      {tenantMark(name)}
    </span>
  );
}

const roleLabels: Record<MembershipProject["role"], string> = {
  owner: "Owner",
  developer: "Developer",
  approver: "Approver",
  viewer: "Viewer",
};

/**
 * Lists the projects the caller belongs to. Switching is a selection only: the pages behind each
 * project arrive with later tasks, and nothing here invents a project the caller is not in.
 */
export function ProjectSwitcher({
  projects,
  currentId,
  collapsed,
  onSelect,
  onCreateProject,
  loading = false,
  buttonClassName,
}: {
  projects: MembershipProject[];
  currentId: string | null;
  collapsed: boolean;
  onSelect: (project: MembershipProject) => void;
  /** Opens the project picker when the caller wants a new project. Absent until wired. */
  onCreateProject?: () => void;
  /** True while the membership list has not arrived, so the control keeps its tab stop. */
  loading?: boolean;
  buttonClassName?: string;
}) {
  const [open, setOpen] = React.useState(false);
  const current = projects.find((project) => project.id === currentId) ?? projects[0] ?? null;
  const container = React.useRef<HTMLDivElement>(null);
  const triggerRef = React.useRef<HTMLButtonElement>(null);
  const menuRef = React.useRef<HTMLDivElement>(null);

  /** Enabled options in DOM order, so traversal skips a disabled "Create project". */
  function menuItems(): HTMLElement[] {
    return [
      ...(menuRef.current?.querySelectorAll<HTMLElement>("[role=menuitem]:not([disabled])") ?? []),
    ];
  }

  /**
   * The same keyboard contract as the shared DropdownMenu: arrows traverse the enabled options,
   * Home and End jump to the ends, Tab dismisses without trapping, Escape dismisses and returns
   * focus to the trigger. The hand-rolled menu previously had none of this.
   */
  function onMenuKeyDown(event: React.KeyboardEvent<HTMLDivElement>) {
    if (event.key === "Escape") {
      event.preventDefault();
      setOpen(false);
      triggerRef.current?.focus();
      return;
    }
    if (event.key === "Tab") {
      // Let the browser move focus on; closing here is what stops the menu trapping it.
      setOpen(false);
      return;
    }
    const items = menuItems();
    if (items.length === 0) return;
    const index = items.indexOf(document.activeElement as HTMLElement);
    const next =
      event.key === "ArrowDown"
        ? items[(index + 1) % items.length]
        : event.key === "ArrowUp"
          ? items[(index - 1 + items.length) % items.length]
          : event.key === "Home"
            ? items[0]
            : event.key === "End"
              ? items[items.length - 1]
              : null;
    if (next) {
      event.preventDefault();
      next.focus();
    }
  }

  React.useEffect(() => {
    if (!open) return;
    const onPointer = (event: MouseEvent) => {
      if (!container.current?.contains(event.target as Node)) setOpen(false);
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onPointer);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onPointer);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  if (!current) {
    if (!loading) {
      return (
        <p className="flex h-[1.875rem] min-w-0 flex-1 items-center px-1.5 text-sm text-muted-foreground">
          {collapsed ? "" : "No projects"}
        </p>
      );
    }
    // Keeps the first tab stop while the list is on its way, so focus order does not jump.
    return (
      <button
        type="button"
        disabled
        aria-label="Switch project"
        className={cn(
          "flex h-[1.875rem] min-w-0 flex-1 items-center px-1.5 text-sm text-muted-foreground",
          buttonClassName,
        )}
      >
        {collapsed ? "" : "Loading projects"}
      </button>
    );
  }

  return (
    <div ref={container} className="relative min-w-0 flex-1">
      <button
        ref={triggerRef}
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={`Switch project, current project ${current.name}`}
        onClick={() => setOpen((value) => !value)}
        onKeyDown={(event) => {
          // The trigger only opened on click, so a keyboard user could open the menu and then
          // had nowhere to go: focus stayed on the trigger and the menu's own arrow handling
          // never ran. Enter, Space, and ArrowDown open it and move to the first row, which is
          // what a menu button is expected to do.
          if (event.key === "Enter" || event.key === " " || event.key === "ArrowDown") {
            event.preventDefault();
            setOpen(true);
            requestAnimationFrame(() => menuItems()[0]?.focus());
          }
        }}
        className={cn(
          "flex h-11 w-full items-center gap-2.5 rounded-lg border border-border/60 bg-card px-2 text-sm shadow-2xs hover:bg-sidebar-accent transition-colors md:h-[2.125rem]",
          collapsed && "justify-center px-0 border-transparent bg-transparent shadow-none",
          buttonClassName,
        )}
      >
        <TenantMark name={current.name} id={current.id} />
        {!collapsed && (
          <>
            <span className="min-w-0 flex-1 truncate text-left font-medium">{current.name}</span>
            <ChevronsUpDown className="size-3.5 shrink-0 text-muted-foreground" aria-hidden />
          </>
        )}
      </button>

      {open && (
        <div
          ref={menuRef}
          role="menu"
          aria-label="Your projects"
          onKeyDown={onMenuKeyDown}
          className="absolute top-9 left-0 z-50 max-h-[min(24rem,60dvh)] w-64 overflow-y-auto rounded-md border bg-popover p-1 text-popover-foreground shadow-md"
        >
          <p className="px-2 py-1 text-xs font-medium text-muted-foreground">Your projects</p>
          {projects.map((project) => {
            const selected = project.id === current.id;
            return (
              <button
                key={project.id}
                type="button"
                role="menuitem"
                onClick={() => {
                  onSelect(project);
                  setOpen(false);
                }}
                className={cn(
                  "flex h-11 w-full items-center gap-2 rounded-sm px-2 text-sm hover:bg-accent md:h-8",
                  selected && "bg-selected font-medium",
                )}
              >
                <TenantMark name={project.name} id={project.id} className="size-5" />
                <span className="min-w-0 flex-1 truncate text-left">{project.name}</span>
                {selected ? (
                  <Check className="size-3.5 text-accent-text" aria-hidden />
                ) : (
                  <span className="text-xs text-muted-foreground">{roleLabels[project.role]}</span>
                )}
              </button>
            );
          })}
          <div className="my-1 h-px bg-border" />
          <button
            type="button"
            role="menuitem"
            disabled={!onCreateProject}
            onClick={() => {
              onCreateProject?.();
              setOpen(false);
            }}
            className="flex h-11 w-full items-center gap-2 rounded-sm px-2 text-sm text-muted-foreground md:h-8"
          >
            <Plus className="size-4" aria-hidden />
            Create project
          </button>
        </div>
      )}
    </div>
  );
}
